import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API } from '../utils/net-api';
import './NewHomePage.css';

const NewHomePage = () => {
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [carouselData, setCarouselData] = useState([]);
    const [loading, setLoading] = useState(true);
    const totalSlides = 5;
    const [isCarouselFixed, setIsCarouselFixed] = useState(false);
    const [isPC, setIsPC] = useState(window.innerWidth > 834);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);

    useEffect(() => {
        document.title = "Marlo - Home";
        console.log('NewHomePage loaded, currentSlide:', currentSlide);
        loadCarouselData();
        
        // 监听窗口大小变化
        const handleResize = () => {
            setIsPC(window.innerWidth > 834);
            setIsMobile(window.innerWidth <= 480);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 加载轮播数据
    const loadCarouselData = async () => {
        try {
            const response = await axios.get(API.homeCarousel);
            if (response.data && response.data.Succeed) {
                setCarouselData(response.data.Data || []);
            } else {
                console.error('Failed to load carousel data:', response.data?.Message);
                // 使用默认数据作为fallback
                setCarouselData([]);
            }
        } catch (error) {
            console.error('Failed to load carousel data:', error);
            // 使用默认数据作为fallback
            setCarouselData([]);
        } finally {
            setLoading(false);
        }
    };

    // 格式化日期时间
    const formatDateTime = (timestamp) => {
        if (!timestamp) return 'TBD';
        const date = new Date(timestamp * 1000); // 转换为毫秒
        const options = { 
            month: 'long', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
        };
        return date.toLocaleDateString('en-US', options).replace(',', ',');
    };

    // 获取轮播数据，如果没有足够数据则使用默认数据填充
    const getSlideData = (index) => {
        const defaultData = [
            { title: "Grab a coffee at local coffee shop with me", location: "FutureWard", description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut", attendeeCount: "34+" },
            { title: "Join our weekend hiking adventure", location: "Golden Gate Park", description: "Join us for an exciting hiking adventure in the beautiful Golden Gate Park", attendeeCount: "12+" },
            { title: "Photography walk in the city center", location: "Union Square", description: "Capture the beauty of the city with fellow photography enthusiasts", attendeeCount: "8+" },
            { title: "Book club meeting this Thursday", location: "Public Library", description: "Discuss the latest bestseller with our friendly book club members", attendeeCount: "15+" },
            { title: "Cooking class for beginners", location: "Community Center", description: "Learn basic cooking skills in a fun and supportive environment", attendeeCount: "6+" }
        ];

        if (carouselData && carouselData[index]) {
            const wave = carouselData[index];
            return {
                title: wave.Title || defaultData[index].title,
                location: wave.DetailedLocation || defaultData[index].location,
                description: wave.Body || defaultData[index].description,
                dateTime: `${wave.EventDateStr}, ${wave.EventTimeStr}` || formatDateTime(wave.EventStartTimestamp),
                attendeeCount: wave.Behaviors && wave.Behaviors.length > 0 ? `${wave.Behaviors.length}+` : defaultData[index].attendeeCount,
                id: wave.ID,
                imageUrl: wave.Media && wave.Media.length > 0 ? wave.Media[0].FilePath : null
            };
        }
        
        return {
            ...defaultData[index],
            dateTime: "September 30, 19:30", // 默认时间
            id: null,
            imageUrl: null
        };
    };

    useEffect(() => {
        console.log('CurrentSlide changed to:', currentSlide);
    }, [currentSlide]);

    // PC端和Pad端滚动吸附效果
    useEffect(() => {
        if (isMobile) return; // 只排除手机端，PC和Pad都使用这套逻辑

        const thirdSection = document.querySelector('.third-section');
        if (!thirdSection) return;

        let isInCarousel = false;
        let wheelTimeout = null;
        let preventReEnter = false;
        let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        let touchStartY = 0;
        let touchEndY = 0;

        const handleScroll = () => {
            const rect = thirdSection.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const thirdSectionTop = thirdSection.offsetTop;
            const thirdSectionBottom = thirdSectionTop + thirdSection.offsetHeight;
            
            // 计算滚动方向
            const scrollDirection = scrollTop > lastScrollTop ? 'down' : 'up';
            lastScrollTop = scrollTop;

            // 如果正在防止重新进入，检查是否已经离开轮播区域足够远
            if (preventReEnter) {
                if (scrollTop < thirdSectionTop - 200 || scrollTop > thirdSectionBottom + 200) {
                    preventReEnter = false;
                }
                return;
            }

            // 当滚动接近第三个section时
            if (!isInCarousel) {
                // 判断当前位置：在轮播上面还是下面（给予缓冲区）
                const isComingFromAbove = scrollTop < thirdSectionTop + 50;
                const isComingFromBelow = scrollTop > thirdSectionBottom - 50;
                
                // 智能吸附逻辑：
                // 1. 从上面接近 + 向下滚动 → 吸附
                // 2. 从下面接近 + 向上滚动 → 吸附
                let shouldAttach = false;
                
                if (isComingFromAbove && scrollDirection === 'down' && scrollTop >= thirdSectionTop - 100) {
                    shouldAttach = true;
                } else if (isComingFromBelow && scrollDirection === 'up' && scrollTop <= thirdSectionBottom + 100) {
                    shouldAttach = true;
                }
                
                if (shouldAttach) {
                    isInCarousel = true;
                    setIsCarouselFixed(true);
                    document.body.style.overflow = 'hidden';
                    window.scrollTo(0, thirdSectionTop);
                }
            }
        };

        const handleWheel = (e) => {
            if (!isCarouselFixed) return;

            e.preventDefault();

            // 节流处理：如果在动画中或节流中，直接返回
            if (wheelTimeout || isAnimating) return;

            const delta = e.deltaY;

            if (delta > 0) {
                // 向下滚动
                if (currentSlide < totalSlides - 1) {
                    // 设置节流
                    wheelTimeout = setTimeout(() => {
                        wheelTimeout = null;
                    }, 750);
                    
                    handleDotClick(currentSlide + 1);
                } else {
                    // 最后一张，解除吸附，滚动到下一个section
                    // 先计算目标位置（在解除fixed之前）
                    const secondSection = document.querySelector('.second-section');
                    const secondSectionBottom = secondSection ? secondSection.offsetTop + secondSection.offsetHeight : 0;
                    const targetPosition = secondSectionBottom + window.innerHeight;
                    
                    setIsCarouselFixed(false);
                    document.body.style.overflow = '';
                    isInCarousel = false;
                    preventReEnter = true;
                    
                    // 直接滚动到目标位置
                    requestAnimationFrame(() => {
                        window.scrollTo({
                            top: targetPosition,
                            behavior: 'smooth'
                        });
                    });
                }
            } else {
                // 向上滚动
                if (currentSlide > 0) {
                    // 设置节流
                    wheelTimeout = setTimeout(() => {
                        wheelTimeout = null;
                    }, 750);
                    
                    handleDotClick(currentSlide - 1);
                } else {
                    // 第一张，解除吸附，回到上一个section
                    // 先计算目标位置（在解除fixed之前）
                    const secondSection = document.querySelector('.second-section');
                    const targetPosition = secondSection ? secondSection.offsetTop + secondSection.offsetHeight - window.innerHeight : 0;
                    
                    setIsCarouselFixed(false);
                    document.body.style.overflow = '';
                    isInCarousel = false;
                    preventReEnter = true;
                    
                    // 直接滚动到目标位置
                    requestAnimationFrame(() => {
                        window.scrollTo({
                            top: Math.max(0, targetPosition),
                            behavior: 'smooth'
                        });
                    });
                }
            }
        };

        // Pad端触摸事件处理
        const handleTouchStart = (e) => {
            if (!isCarouselFixed) return;
            touchStartY = e.touches[0].clientY;
        };

        const handleTouchMove = (e) => {
            if (!isCarouselFixed) return;
            touchEndY = e.touches[0].clientY;
        };

        const handleTouchEnd = () => {
            if (!isCarouselFixed) return;
            
            const deltaY = touchStartY - touchEndY;

            // 需要一定的滑动距离
            if (Math.abs(deltaY) < 50) return;

            // 如果正在动画中，不处理
            if (isAnimating) return;

            if (deltaY > 0) {
                // 向上滑动（下一张）
                if (currentSlide < totalSlides - 1) {
                    handleDotClick(currentSlide + 1);
                } else {
                    // 最后一张，解除吸附
                    const secondSection = document.querySelector('.second-section');
                    const secondSectionBottom = secondSection ? secondSection.offsetTop + secondSection.offsetHeight : 0;
                    const targetPosition = secondSectionBottom + window.innerHeight;
                    
                    setIsCarouselFixed(false);
                    document.body.style.overflow = '';
                    isInCarousel = false;
                    preventReEnter = true;
                    
                    requestAnimationFrame(() => {
                        window.scrollTo({
                            top: targetPosition,
                            behavior: 'smooth'
                        });
                    });
                }
            } else {
                // 向下滑动（上一张）
                if (currentSlide > 0) {
                    handleDotClick(currentSlide - 1);
                } else {
                    // 第一张，解除吸附
                    const secondSection = document.querySelector('.second-section');
                    const targetPosition = secondSection ? secondSection.offsetTop + secondSection.offsetHeight - window.innerHeight : 0;
                    
                    setIsCarouselFixed(false);
                    document.body.style.overflow = '';
                    isInCarousel = false;
                    preventReEnter = true;
                    
                    requestAnimationFrame(() => {
                        window.scrollTo({
                            top: Math.max(0, targetPosition),
                            behavior: 'smooth'
                        });
                    });
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('wheel', handleWheel, { passive: false });
        window.addEventListener('touchstart', handleTouchStart, { passive: false });
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd, { passive: false });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
            document.body.style.overflow = '';
        };
    }, [isMobile, isCarouselFixed, currentSlide, totalSlides, isAnimating]);

    // 手机端滚动吸附效果
    useEffect(() => {
        if (!isMobile) return;

        const thirdSection = document.querySelector('.third-section');
        if (!thirdSection) return;

        let isInCarousel = false;
        let touchStartY = 0;
        let touchEndY = 0;

        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const thirdSectionTop = thirdSection.offsetTop;

            // 当滚动到第三个section时，触发吸附
            if (scrollTop >= thirdSectionTop - 100 && !isInCarousel && !isCarouselFixed) {
                isInCarousel = true;
                setIsCarouselFixed(true);
                document.body.style.overflow = 'hidden';
                document.body.style.position = 'fixed';
                document.body.style.width = '100%';
                document.body.style.top = `-${scrollTop}px`;
            }
        };

        const handleTouchStart = (e) => {
            if (!isCarouselFixed) return;
            touchStartY = e.touches[0].clientY;
        };

        const handleTouchMove = (e) => {
            if (!isCarouselFixed) return;
            touchEndY = e.touches[0].clientY;
        };

        const handleTouchEnd = () => {
            if (!isCarouselFixed) return;
            
            const deltaY = touchStartY - touchEndY;

            // 需要一定的滑动距离
            if (Math.abs(deltaY) < 50) return;

            // 如果正在动画中，不处理
            if (isAnimating) return;

            if (deltaY > 0) {
                // 向上滑动（下一张）
                if (currentSlide < totalSlides - 1) {
                    handleDotClick(currentSlide + 1);
                } else {
                    // 最后一张，解除吸附
                    // 先计算目标位置（在解除fixed之前）
                    const secondSection = document.querySelector('.second-section');
                    const secondSectionBottom = secondSection ? secondSection.offsetTop + secondSection.offsetHeight : 0;
                    const targetPosition = secondSectionBottom + window.innerHeight;
                    
                    setIsCarouselFixed(false);
                    document.body.style.overflow = '';
                    document.body.style.position = '';
                    document.body.style.width = '';
                    document.body.style.top = '';
                    isInCarousel = false;
                    
                    // 直接滚动到目标位置
                    requestAnimationFrame(() => {
                        window.scrollTo({
                            top: targetPosition,
                            behavior: 'smooth'
                        });
                    });
                }
            } else {
                // 向下滑动（上一张）
                if (currentSlide > 0) {
                    handleDotClick(currentSlide - 1);
                } else {
                    // 第一张，解除吸附回到上一个section
                    // 先计算目标位置（在解除fixed之前）
                    const secondSection = document.querySelector('.second-section');
                    const targetPosition = secondSection ? secondSection.offsetTop + secondSection.offsetHeight - window.innerHeight : 0;
                    
                    setIsCarouselFixed(false);
                    document.body.style.overflow = '';
                    document.body.style.position = '';
                    document.body.style.width = '';
                    document.body.style.top = '';
                    isInCarousel = false;
                    
                    // 直接滚动到目标位置
                    requestAnimationFrame(() => {
                        window.scrollTo({
                            top: Math.max(0, targetPosition),
                            behavior: 'smooth'
                        });
                    });
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('touchstart', handleTouchStart, { passive: true });
        window.addEventListener('touchmove', handleTouchMove, { passive: true });
        window.addEventListener('touchend', handleTouchEnd);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
        };
    }, [isMobile, isCarouselFixed, currentSlide, totalSlides, isAnimating]);

    const handleMoreEventsClick = () => {
        navigate('/event');
    };

    const handleDotClick = (index) => {
        console.log('Dot clicked:', index, 'Current:', currentSlide, 'Animating:', isAnimating);
        if (isAnimating || index === currentSlide) {
            console.log('Click ignored - same slide or animating');
            return;
        }
        
        console.log('Setting slide to:', index);
        setIsAnimating(true);
        
        // 先让当前内容渐隐并向上移动
        setTimeout(() => {
            setCurrentSlide(index);
        }, 100);
        
        // 动画持续时间后重置动画状态
        setTimeout(() => {
            setIsAnimating(false);
            console.log('Animation finished');
        }, 700);
    };

    return (
        <div className="new-home-page">
            <Header />
            
            <main className="home-main">
                {/* First module */}
                <div className="hero-section">
                    {/* 光晕背景效果 */}
                    <div className="hero-glow-effect"></div>
                    <div className="hero-glow-effect2"></div>
                    
                    <img 
                        src={require('../images/home/card1.png')} 
                        alt="Card 1" 
                        className="hero-card1"
                    />
                    <img 
                        src={require('../images/home/card2.png')} 
                        alt="Card 2" 
                        className="hero-card2"
                    />
                    <img 
                        src={require('../images/home/card3.png')} 
                        alt="Card 3" 
                        className="hero-card3"
                    />
                    <img 
                        src={require('../images/home/cards.png')} 
                        alt="Mobile Cards" 
                        className="hero-mobile-cards"
                    />
                    <div className="hero-text-group">
                        <div className="hero-text-line1">MARLO</div>
                        <div className="hero-text-line2">Find your</div>
                        <div className="hero-text-line3">people nearby</div>
                        <div className="hero-text-line4">
                            Marlo isn't just about events or matching—it's about instantly connecting real people <br />through real activities nearby.
                        </div>
                    </div>
                </div>
                
                {/* Second module */}
                <div className="second-section">
                    <div className="second-overlay">
                        <div className="second-text-group">
                            <div className="second-text-line1">
                                <span className="pc-text">Join us in shaping the next evolution of social connection.</span>
                                <span className="mobile-text">
                                    <span>Join us in shaping</span><br/>
                                    <span>the next evolution</span><br/>
                                    <span>of social</span><br/>
                                    <span>connection.</span>
                                </span>
                            </div>
                            <div className="second-text-line2" onClick={() => navigate('/business')} style={{ cursor: 'pointer' }}>
                                <div className="second-text-container">
                                    <span className="second-text">Business Solutions</span>
                                    <div className="second-text-underline"></div>
                                </div>
                                <img 
                                    src={require('../images/home/right.png')} 
                                    alt="Right Arrow" 
                                    className="second-icon"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Third module */}
                {/* 占位元素，当轮播固定时保持页面高度 */}
                {isCarouselFixed && (isPC || isMobile) && <div style={{ height: '100vh', width: '100%' }} />}
                <div className={`third-section ${isCarouselFixed && (isPC || isMobile) ? 'fixed-carousel' : ''}`}>
                    {/* 动态模糊背景 - 为每个slide预渲染背景 */}
                    {[0, 1, 2, 3, 4].map((bgIndex) => (
                        <div 
                            key={`bg-${bgIndex}`}
                            className="third-section-bg"
                            style={{
                                backgroundImage: getSlideData(bgIndex).imageUrl 
                                    ? `url(${getSlideData(bgIndex).imageUrl})` 
                                    : 'url(../images/home/bg3.png)',
                                opacity: bgIndex === currentSlide ? 1 : 0,
                                transition: 'opacity 0.7s ease-in-out'
                            }}
                        />
                    ))}
                    <div className="third-carousel-container">
                        {[0, 1, 2, 3, 4].map((slideIndex) => (
                            <div 
                                key={slideIndex}
                                className={`third-content ${slideIndex === currentSlide ? 'active' : ''} ${isAnimating ? 'animating' : ''}`}
                                style={{
                                    transform: slideIndex === currentSlide ? 'translateY(0)' : 
                                              slideIndex < currentSlide ? 'translateY(-100%)' : 'translateY(100%)',
                                    opacity: slideIndex === currentSlide ? 1 : 0
                                }}
                            >
                                {/* First block */}
                                <div className="third-first-block">
                                    <div className="third-title">
                                        {getSlideData(slideIndex).title}
                                    </div>
                                    <div className="third-meta-row">
                                        <div className="third-meta-item">
                                            <img 
                                                src={require('../images/home/time.png')} 
                                                alt="Time" 
                                                className="third-meta-icon"
                                            />
                                            <span className="third-meta-text">
                                                {getSlideData(slideIndex).dateTime}
                                            </span>
                                        </div>
                                        <div className="third-meta-item">
                                            <img 
                                                src={require('../images/home/addr.png')} 
                                                alt="Location" 
                                                className="third-meta-icon"
                                            />
                                            <span className="third-meta-text">
                                                {getSlideData(slideIndex).location}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="third-divider"></div>
                                    <div className="third-description">
                                        {getSlideData(slideIndex).description}
                                    </div>
                                    <div className="third-avatars">
                                        {(() => {
                                            const slideData = getSlideData(slideIndex);
                                            const behaviors = carouselData && carouselData[slideIndex] ? carouselData[slideIndex].Behaviors : [];
                                            const attendeeCount = behaviors.length;
                                            
                                            if (attendeeCount === 0) {
                                                // 没有参与者时显示默认头像
                                                return (
                                                    <div className="third-avatar third-avatar-default">
                                                        <img 
                                                            src="/images/avatar_no.png" 
                                                            alt="No attendees"
                                                            className="third-avatar-image"
                                                        />
                                                    </div>
                                                );
                                            } else {
                                                // 有参与者时显示头像
                                                const limit = Math.min(attendeeCount, 4);
                                                const avatars = [];
                                                
                                                for (let i = 0; i < limit; i++) {
                                                    const user = behaviors[i].User;
                                                    avatars.push(
                                                        <div key={i} className="third-avatar" style={{ zIndex: limit - i }}>
                                                            <img 
                                                                src={user?.ProfilePicture || require('../images/avatar_default.png')} 
                                                                alt={user?.Name || 'User'}
                                                                className="third-avatar-image"
                                                                onError={(e) => {
                                                                    e.target.onerror = null; // 防止无限循环
                                                                    e.target.src = require('../images/avatar_default.png');
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                }
                                                
                                                // 如果超过4个参与者，显示"+数字"
                                                if (attendeeCount > 4) {
                                                    avatars.push(
                                                        <div key="more" className="third-avatar third-avatar-count" style={{ zIndex: 0 }}>
                                                            {attendeeCount - 4}+
                                                        </div>
                                                    );
                                                }
                                                
                                                return avatars;
                                            }
                                        })()}
                                    </div>
                                    <button 
                                        className="third-learn-more"
                                        onClick={() => {
                                            const slideData = getSlideData(slideIndex);
                                            if (slideData.id) {
                                                navigate(`/event/${slideData.id}`);
                                            }
                                        }}
                                    >
                                        Learn More
                                    </button>
                                    
                                    {/* More Events - 移到 first-block 内部 */}
                                    <div className="third-more-events-inline" onClick={handleMoreEventsClick}>
                                        <div className="third-more-text-container">
                                            <span className="third-more-text">More Events</span>
                                            <div className="third-more-underline"></div>
                                        </div>
                                        <img 
                                            src={require('../images/home/right.png')} 
                                            alt="Right Arrow" 
                                            className="third-more-icon"
                                        />
                                    </div>
                                </div>
                                
                                {/* Second block */}
                                <div className="third-second-block">
                                    {getSlideData(slideIndex).imageUrl && (
                                        <img 
                                            src={getSlideData(slideIndex).imageUrl} 
                                            alt={getSlideData(slideIndex).title}
                                            className="third-event-image"
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Carousel Controls */}
                    <div className="third-carousel-controls">
                        {[0, 1, 2, 3, 4].map((index) => (
                            <div 
                                key={index}
                                className={`carousel-dot ${index === currentSlide ? 'carousel-dot-active' : ''}`}
                                onClick={() => handleDotClick(index)}
                            ></div>
                        ))}
                    </div>
                    
                    {/* More Events */}
                    <div className="third-more-events" onClick={handleMoreEventsClick}>
                        <div className="third-more-text-container">
                            <span className="third-more-text">More Events</span>
                            <div className="third-more-underline"></div>
                        </div>
                        <img 
                            src={require('../images/home/right.png')} 
                            alt="Right Arrow" 
                            className="third-more-icon"
                        />
                    </div>
                </div>
                
                {/* Fourth module - Download */}
                <div className="fourth-section">
                    <div className="fourth-content">
                        {/* Top notification */}
                        <div className="fourth-notification">
                        </div>
                        
                        {/* Main content */}
                        <div className="fourth-main">
                            <div className="fourth-title-group">
                                <div className="fourth-title-line1">
                                    <span className="fourth-title-text">Download</span>
                                    <span className="fourth-title-gradient">Marlo</span>
                                    <span className="fourth-title-text">to make friends</span>
                                </div>
                                <div className="fourth-title-line2">and have fun.</div>
                            </div>
                            
                            <img 
                                src={require('../images/home/download.png')} 
                                alt="Download" 
                                className="fourth-download-icon"
                                onClick={() => window.open('https://apps.apple.com/us/app/marlo-connect-meet-nearby/id6450737280?l=zh-Hans-CN', '_blank')}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>
            </main>
            
            <Footer />
        </div>
    );
};

export default NewHomePage;
